import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, firstValueFrom} from "rxjs";
import {getSupportedLanguage, UiUtils} from "../utils/ui.utils";
import moment from "moment";
import {TranslateService} from "@ngx-translate/core";
import {CountrySettingsDto} from "../../core/dto/country-settings-dto";
import * as Sentry from "@sentry/angular";
import {ExternalApiService} from "../../core/api/external-api/external-api.service";

@Injectable({
  providedIn: 'root'
})
export class I18nService {

  private translate = inject(TranslateService);
  private externalApiService = inject(ExternalApiService);
  private language$ = new BehaviorSubject<string>(getSupportedLanguage());

  private countrySettings: Map<string, CountrySettingsDto|Promise<CountrySettingsDto>> = new Map<string, CountrySettingsDto>();

  constructor() {
    this.translate.setDefaultLang(UiUtils.FALLBACK_LANG);
  }

  async setLanguage(language: string) {
    const supportedLanguage = getSupportedLanguage(language);
    this.translate.use(supportedLanguage);
    this.language$.next(supportedLanguage);
    await UiUtils.registerLocale(supportedLanguage);
    moment.locale(supportedLanguage);
    document.documentElement.setAttribute('dir', supportedLanguage === 'ar' ? 'rtl' : 'auto');
  }

  getLanguage() {
    return this.language$.getValue();
  }

  public async getCountrySettings(countryCode: string): Promise<CountrySettingsDto> {
    if (this.countrySettings.has(countryCode)) {
      return this.countrySettings.get(countryCode)!;
    }

    let countrySettings = CountrySettingsDto.DEFAULT;
    try {
      const settings = await firstValueFrom(this.externalApiService.getCountrySettings(countryCode));
      this.countrySettings.set(countryCode, settings);
      return this.countrySettings.get(countryCode)!;
    } catch (err: any) {
      if (err.status !== 0) {
        Sentry.captureException(new Error('Unable to get country settings'), {extra: {country: countryCode}})
      }
    }
    return countrySettings;
  }
}
